import type {
  AnalyticsEvent,
  AnalyticsImplementation,
  ConfigApi,
} from '@backstage/frontend-plugin-api';
import { MatomoAnalytics as JanusMatomoAnalytics } from '@janus-idp/backstage-plugin-analytics-module-matomo';

type MatomoCommand = [string, ...(string | number | undefined)[]];

type DimensionKey = 'resourceId' | 'resourceName' | 'resourceType';

type Dimension = {
  key: DimensionKey;
  id: number;
};

export class MatomoAnalytics implements AnalyticsImplementation {
  private constructor(private readonly dimensions: Dimension[]) {}

  static fromConfig(configApi: ConfigApi): MatomoAnalytics {
    const dimensions = (
      [
        ['resourceId', 'app.analytics.matomo.customDimensions.resourceId'],
        ['resourceName', 'app.analytics.matomo.customDimensions.resourceName'],
        ['resourceType', 'app.analytics.matomo.customDimensions.resourceType'],
      ] as const
    )
      .map(([key, configPath]) => ({
        key,
        id: configApi.getOptionalNumber(configPath),
      }))
      .filter(
        (dimension): dimension is Dimension => dimension.id !== undefined,
      );

    JanusMatomoAnalytics.fromConfig(configApi);
    return new MatomoAnalytics(dimensions);
  }

  captureEvent(event: AnalyticsEvent): void {
    const matomoQueue = (window as Window & { _paq?: MatomoCommand[] })._paq;
    if (!matomoQueue) {
      return;
    }

    const { extension } = event.context;
    const category =
      typeof extension === 'string' && extension ? extension : 'App';

    for (const dimension of this.dimensions) {
      const value = event.attributes?.[dimension.key];
      matomoQueue.push(
        value === undefined
          ? ['deleteCustomDimension', dimension.id]
          : ['setCustomDimension', dimension.id, String(value)],
      );
    }

    matomoQueue.push([
      'trackEvent',
      category,
      event.action,
      event.subject,
      event.value,
    ]);

    for (const dimension of this.dimensions) {
      matomoQueue.push(['deleteCustomDimension', dimension.id]);
    }
  }
}
